import { ArpSavedFormatModelDto } from '@treasury/api/channel';
import {
    ARP_FIELD_DEFINITIONS,
    ARP_GLOSSARY,
    type ArpFormatContainer,
    type ArpFormatDesignerModel,
    type ArpFormatTile,
    type ArpGlossaryGroup,
} from './arp-upload-format.types';

const FILLER_TILE: ArpFormatTile = {
    id: '__filler__',
    name: 'Filler Tile',
    text: '__filler__',
    type: 'filler',
    optional: 'true',
    active: 'true',
    width: 1,
};

/**
 * For FixedPosition formats, inserts filler tiles into any gaps between active tile ranges
 * so the drag-and-drop component preserves non-contiguous positions.
 */
function addFillerTiles(activeTiles: ArpFormatTile[]): ArpFormatTile[] {
    if (activeTiles.length === 0) {
        return activeTiles;
    }

    const maxEnd = Math.max(...activeTiles.map(t => t.endPosition ?? 0));
    const slots: (ArpFormatTile | undefined)[] = new Array(maxEnd).fill(undefined);

    for (const tile of activeTiles) {
        const begin = (tile.beginPosition ?? 1) - 1;
        const end = (tile.endPosition ?? 1) - 1;
        for (let i = begin; i <= end; i++) {
            slots[i] = tile;
        }
    }

    const condensed: ArpFormatTile[] = [];
    let prev: ArpFormatTile | undefined = undefined;
    for (const slot of slots) {
        if (slot === undefined) {
            if (prev === undefined || prev.type !== 'filler') {
                condensed.push({ ...FILLER_TILE });
            }
            prev = condensed[condensed.length - 1];
        } else if (prev === undefined || prev.id !== slot.id) {
            condensed.push(slot);
            prev = slot;
        }
    }

    for (let i = 0; i < condensed.length; i++) {
        if (condensed[i].type !== 'filler') {
            continue;
        }

        const prevTile = condensed[i - 1];
        const nextTile = condensed[i + 1];
        const beginPosition = prevTile ? (prevTile.endPosition ?? 0) + 1 : 1;
        const endPosition = nextTile ? (nextTile.beginPosition ?? 1) - 1 : beginPosition;
        const width = endPosition - beginPosition + 1;

        condensed[i] = { ...FILLER_TILE, beginPosition, endPosition, width };
    }

    return condensed;
}

/**
 * Maps the flat DTO fields to designer tile objects.
 * Each ARP field has order/begin/end properties on the DTO keyed by field name.
 */
function buildTilesFromDto(dto: ArpSavedFormatModelDto): {
    activeTiles: ArpFormatTile[];
    inactiveTiles: ArpFormatTile[];
} {
    const isFixed = dto.type === 'FixedPosition';
    const activeTiles: ArpFormatTile[] = [];
    const inactiveTiles: ArpFormatTile[] = [];

    for (const field of ARP_FIELD_DEFINITIONS) {
        const orderKey = `${field.key}Order` as keyof ArpSavedFormatModelDto;
        const beginKey = `${field.key}Begin` as keyof ArpSavedFormatModelDto;
        const endKey = `${field.key}End` as keyof ArpSavedFormatModelDto;

        const order = (dto[orderKey] ?? undefined) as number | undefined;
        const begin = (dto[beginKey] ?? undefined) as number | undefined;
        const end = (dto[endKey] ?? undefined) as number | undefined;

        const isActive = isFixed ? begin !== undefined && end !== undefined : order !== undefined;

        const tile: ArpFormatTile = {
            id: field.key,
            name: field.name,
            text: field.key,
            type: field.group,
            optional: (!field.required).toString(),
            active: isActive ? 'true' : 'false',
            order: isActive ? order : undefined,
            beginPosition: isActive ? begin : undefined,
            endPosition: isActive ? end : undefined,
            width: isActive && begin !== undefined && end !== undefined ? end - begin + 1 : 1,
        };

        if (isActive) {
            activeTiles.push(tile);
        } else {
            inactiveTiles.push(tile);
        }
    }

    // Sort active tiles by position/order
    if (isFixed) {
        activeTiles.sort((a, b) => (a.beginPosition ?? 0) - (b.beginPosition ?? 0));
    } else {
        activeTiles.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    const finalActive = isFixed ? addFillerTiles(activeTiles) : activeTiles;

    return { activeTiles: finalActive, inactiveTiles };
}

/**
 * Encodes the item amount format from the DTO's two fields into a single UI value.
 *
 * | UI Label          | itemAmountFormat      | itemAmountDecimal |
 * |-------------------|-----------------------|-------------------|
 * | DecimalIncluded   | "DecimalIncluded"     | (ignored)         |
 * | WholeDollar       | "DecimalNotIncluded"  | true              |
 * | ImpliedDecimal    | "DecimalNotIncluded"  | false             |
 */
export function encodeItemAmountFormat(dto: ArpSavedFormatModelDto): string {
    if (dto.itemAmountFormat === 'DecimalIncluded') {
        return 'DecimalIncluded';
    }
    if (dto.itemAmountDecimal === true) {
        return 'WholeDollar';
    }
    return 'ImpliedDecimal';
}

/**
 * Decodes a single UI item amount value back to the DTO's two fields.
 */
export function decodeItemAmountFormat(uiValue: string): {
    itemAmountFormat: string;
    itemAmountDecimal: boolean;
} {
    if (uiValue === 'DecimalIncluded') {
        return { itemAmountFormat: 'DecimalIncluded', itemAmountDecimal: false };
    }
    if (uiValue === 'WholeDollar') {
        return { itemAmountFormat: 'DecimalNotIncluded', itemAmountDecimal: true };
    }
    // ImpliedDecimal
    return { itemAmountFormat: 'DecimalNotIncluded', itemAmountDecimal: false };
}

/**
 * Converts a flat ArpSavedFormatModelDto into the designer UI model.
 */
export function formatDtoToDesigner(dto: ArpSavedFormatModelDto): ArpFormatDesignerModel {
    const { activeTiles, inactiveTiles } = buildTilesFromDto(dto);
    const containers: ArpFormatContainer[] = [
        { id: 'in', title: 'Drag and drop items to reorder columns', active: 'true' },
        {
            id: 'out',
            title: 'Drag and drop optional items to the space above to activate',
            active: 'false',
        },
    ];

    return {
        id: dto.id,
        title: 'Issued Items Template Formatting Tool',
        type: dto.type ?? 'Delimited',
        name: dto.name ?? '',
        delimiter: dto.fieldDelimiter ?? ',',
        excludeHeaderRows: dto.headerRows ?? 0,
        excludeFooterRows: dto.footerRows ?? 0,
        itemAmountFormat: encodeItemAmountFormat(dto),
        effectiveDateFormat: dto.issueDateFormat,
        containers,
        tiles: [...activeTiles, ...inactiveTiles, { ...FILLER_TILE, active: 'false' }],
        textQualifier: dto.textQualifier,
        issueDateFormat: dto.issueDateFormat,
        voidDateFormat: dto.voidDateFormat,
        accountTypeCheckingEquals: dto.accountTypeCheckingEquals,
        accountTypeSavingsEquals: dto.accountTypeSavingsEquals,
        voidYesIndicator: dto.voidYesIndicator,
    };
}

/**
 * Flattens the designer model back into a DTO for save/update.
 * Preserves non-designer fields from the original DTO.
 */
export function formatDesignerToDto(
    designerFormat: ArpFormatDesignerModel,
    originalDto?: ArpSavedFormatModelDto
): ArpSavedFormatModelDto {
    const isFixed = designerFormat.type === 'FixedPosition';
    const activeTiles = designerFormat.tiles.filter(
        t => t.active === 'true' && t.type !== 'filler'
    );

    const { itemAmountFormat, itemAmountDecimal } = decodeItemAmountFormat(
        designerFormat.itemAmountFormat
    );

    // Start with a blank DTO shape
    const dto: ArpSavedFormatModelDto = {
        id: designerFormat.id,
        name: designerFormat.name,
        type: designerFormat.type,
        fieldDelimiter: designerFormat.delimiter,
        headerRows: designerFormat.excludeHeaderRows,
        footerRows: designerFormat.excludeFooterRows,
        textQualifier: designerFormat.textQualifier ?? originalDto?.textQualifier,
        itemAmountFormat,
        itemAmountDecimal,
        issueDateFormat:
            designerFormat.effectiveDateFormat ??
            designerFormat.issueDateFormat ??
            originalDto?.issueDateFormat,
        voidDateFormat: designerFormat.voidDateFormat ?? originalDto?.voidDateFormat,
        accountTypeCheckingEquals:
            designerFormat.accountTypeCheckingEquals ?? originalDto?.accountTypeCheckingEquals,
        accountTypeSavingsEquals:
            designerFormat.accountTypeSavingsEquals ?? originalDto?.accountTypeSavingsEquals,
        voidYesIndicator: designerFormat.voidYesIndicator ?? originalDto?.voidYesIndicator,
        updatedBy: originalDto?.updatedBy,
        updatedDate: originalDto?.updatedDate,
    };

    // Map active tiles back to flat DTO field properties
    for (const tile of activeTiles) {
        const key = tile.text; // The field key (e.g., 'itemNumber')
        const orderKey = `${key}Order` as keyof ArpSavedFormatModelDto;
        const beginKey = `${key}Begin` as keyof ArpSavedFormatModelDto;
        const endKey = `${key}End` as keyof ArpSavedFormatModelDto;

        if (isFixed) {
            (dto as unknown as Record<string, unknown>)[beginKey] = Number(tile.beginPosition);
            (dto as unknown as Record<string, unknown>)[endKey] = Number(tile.endPosition);
        } else {
            (dto as unknown as Record<string, unknown>)[orderKey] = Number(tile.order);
        }
    }

    return dto;
}

/**
 * Returns a default format with all tiles in the inactive "out" container.
 */
export function getDefaultFormat(): ArpFormatDesignerModel {
    const tiles: ArpFormatTile[] = ARP_FIELD_DEFINITIONS.map(field => ({
        id: field.key,
        name: field.name,
        text: field.key,
        type: field.group,
        optional: (!field.required).toString(),
        active: 'false',
        width: 1,
    }));

    return {
        id: 0,
        title: 'New Issued Items Upload Format',
        type: 'Delimited',
        name: '',
        delimiter: ',',
        excludeHeaderRows: 0,
        excludeFooterRows: 0,
        itemAmountFormat: 'DecimalIncluded',
        containers: [
            { id: 'in', title: 'Drag and drop items to reorder columns', active: 'true' },
            {
                id: 'out',
                title: 'Drag and drop optional items to the space above to activate',
                active: 'false',
            },
        ],
        tiles: [...tiles, { ...FILLER_TILE, active: 'false' }],
    };
}

/**
 * Returns the ARP glossary for the file format designer.
 */
export function getArpGlossary(): ArpGlossaryGroup[] {
    return ARP_GLOSSARY;
}
